<template>
  <div class="page">
    <header class="header">
      <button class="back-btn" @click="router.push('/')">
        <ArrowLeft size="20" />
      </button>
      <h1 class="page-title">个人中心</h1>
      <div class="header-placeholder"></div>
    </header>

    <div class="content">
      <!-- 未登录状态 -->
      <div v-if="!isLoggedIn" class="login-section">
        <div class="login-card">
          <div class="login-icon">
            <User size="48" />
          </div>
          <h2 class="login-title">登录账户</h2>
          <p class="login-subtitle">登录后查看个人信息和订单数据</p>
          
          <div class="login-form">
            <div class="form-group">
              <label class="form-label">用户名</label>
              <input 
                type="text" 
                v-model="loginForm.username" 
                class="form-input"
                placeholder="请输入用户名"
              />
            </div>
            <div class="form-group">
              <label class="form-label">密码</label>
              <input 
                type="password" 
                v-model="loginForm.password" 
                class="form-input"
                placeholder="请输入密码"
              />
            </div>
            <button 
              class="login-btn" 
              @click="login"
              :disabled="loading"
            >
              <span v-if="loading">登录中...</span>
              <span v-else>登录</span>
            </button>
            <div class="login-register">
              <span>还没有账户？</span>
              <button class="register-link">
                联系管理员
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 登录状态 -->
      <div v-else class="user-section">
        <!-- 用户信息 -->
        <div class="user-card">
          <div class="user-avatar">
            <span class="avatar-text">{{ userInfo.username?.charAt(0) || 'U' }}</span>
          </div>
          <div class="user-info">
            <h2 class="user-name">{{ userInfo.username || '用户' }}</h2>
            <p class="user-role">{{ userInfo.role || '打手' }}</p>
          </div>
          <button class="logout-btn" @click="logout">
            <LogOut size="16" />
          </button>
        </div>

        <!-- 数据概览 -->
        <div class="stats-cards">
          <div class="stat-card">
            <div class="stat-icon">
              <ClipboardList size="20" />
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ stats.totalOrders }}</span>
              <span class="stat-label">总订单</span>
            </div>
          </div>
          <div class="stat-card">
          <div class="stat-icon">
            <Wallet size="20" />
          </div>
          <div class="stat-info">
            <span class="stat-value">¥{{ stats.totalEarnings.toFixed(1) }}</span>
            <span class="stat-label">总收益</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <Calendar size="20" />
          </div>
          <div class="stat-info">
            <span class="stat-value">¥{{ stats.monthlyEarnings.toFixed(1) }}</span>
            <span class="stat-label">本月收益</span>
          </div>
        </div>
          <div class="stat-card">
            <div class="stat-icon">
              <Star size="20" />
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ stats.rating }}★</span>
              <span class="stat-label">评分</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">
              <Calendar size="20" />
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ todayOrderCount }}</span>
              <span class="stat-label">今日订单</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">
              <DollarSign size="20" />
            </div>
            <div class="stat-info">
              <span class="stat-value">¥{{ todayEarnings.toFixed(1) }}</span>
              <span class="stat-label">今日收益</span>
            </div>
          </div>
        </div>

        <!-- 订单管理 -->
        <div class="section">
          <h3 class="section-title">
            <ClipboardList size="18" class="title-icon" />
            订单管理
          </h3>
          <div class="order-tabs">
            <button 
              class="tab-btn"
              :class="{ active: activeOrderTab === 'accepted' }"
              @click="activeOrderTab = 'accepted'"
            >
              已接订单
            </button>
            <button 
              class="tab-btn"
              :class="{ active: activeOrderTab === 'completed' }"
              @click="activeOrderTab = 'completed'"
            >
              今日完成
            </button>
            <button 
              class="tab-btn"
              :class="{ active: activeOrderTab === 'transferred' }"
              @click="activeOrderTab = 'transferred'"
            >
              转单
            </button>
          </div>

          <!-- 已接订单 -->
          <div v-if="activeOrderTab === 'accepted'" class="order-list">
            <div class="loading-state" v-if="loading">
              <div class="spinner"></div>
              <span>加载中...</span>
            </div>
            <div class="empty-state" v-else-if="acceptedOrders.length === 0">
              <ClipboardList size="48" class="empty-icon" />
              <p>暂无已接订单</p>
            </div>
            <div class="order-card" v-else v-for="order in acceptedOrders" :key="order.id">
              <div class="order-info">
                <div class="order-info-row">
                  <span class="info-label">订单ID</span>
                  <span class="info-value">#{{ order.id }}</span>
                </div>
                <div class="order-info-row boss-id-row">
                  <span class="info-label">老板游戏ID</span>
                  <div class="boss-id-wrapper">
                    <span class="boss-id">{{ order.game_id || '-' }}</span>
                    <button 
                      v-if="order.game_id"
                      class="copy-boss-id-btn" 
                      @click="copyBossId(order.game_id)"
                      :class="{ copied: copiedBossId === order.game_id }"
                    >
                      <Copy size="14" />
                    </button>
                  </div>
                </div>
                <div class="order-info-row">
                  <span class="info-label">商品内容</span>
                  <span class="info-value desc-text">{{ order.desc || order.content || '无' }}</span>
                </div>
              </div>
              <div class="order-main">
                <div class="order-title">{{ order.title }}</div>
                <div class="order-price">¥{{ order.price }}</div>
              </div>
              <div class="order-footer">
                <button
                  class="chat-btn"
                  @click="openChat(order)"
                >
                  <MessageSquare size="14" />
                  聊天
                </button>
                <button
                  class="transfer-btn"
                  @click="showTransferDialog(order)"
                >
                  转单
                </button>
                <button
                  v-if="order.status !== 'pending_review'"
                  class="complete-btn"
                  @click="showCompleteConfirm(order)"
                >
                  结单
                </button>
                <span v-else class="review-status">待审核</span>
              </div>
            </div>
          </div>

          <!-- 结单确认弹窗 -->
          <div v-if="showCompleteDialog" class="confirm-overlay" @click.self="closeCompleteDialog">
            <div class="confirm-card">
              <div class="confirm-header">
                <h3>确认结单</h3>
              </div>
              <div class="confirm-body">
                <p>订单名称：{{ completeTargetOrder?.title }}</p>
                <p>订单金额：¥{{ completeTargetOrder?.price }}</p>
                <p class="earnings-info">
                  实际收益（×0.83）：<span class="earnings-value">¥{{ ((completeTargetOrder?.price || 0) * 0.83).toFixed(1) }}</span>
                </p>
              </div>
              <div class="confirm-footer">
                <button class="cancel-btn" @click="closeCompleteDialog">取消</button>
                <button class="confirm-btn" @click="confirmComplete">确认结单</button>
              </div>
            </div>
          </div>

          <!-- 转单弹窗 -->
          <div v-if="transferDialogVisible" class="confirm-overlay" @click.self="closeTransferDialog">
            <div class="confirm-card">
              <div class="confirm-header">
                <h3>转单</h3>
              </div>
              <div class="confirm-body" v-if="!isWrongOrderConfirmed">
                <p class="transfer-question">是否是点错单子？</p>
                <div class="transfer-buttons">
                  <button class="confirm-btn" @click="confirmWrongOrder">是</button>
                  <button class="cancel-btn" @click="confirmNotWrongOrder">否</button>
                </div>
              </div>
              <div class="confirm-body" v-else>
                <p class="transfer-question">请填写进度信息</p>
                <div class="form-group">
                  <label>进度（填多少W）</label>
                  <input type="number" v-model="transferForm.progress" placeholder="例如：5" min="0" />
                </div>
                <div class="form-group">
                  <label>炸单数</label>
                  <input type="number" v-model="transferForm.bombCount" placeholder="例如：2" min="0" />
                </div>
                <div class="form-group">
                  <label>是否是监狱</label>
                  <div class="radio-group">
                    <label class="radio-label">
                      <input type="radio" v-model="transferForm.isPrison" :value="true" /> 是
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="transferForm.isPrison" :value="false" /> 否
                    </label>
                  </div>
                </div>
                <div class="form-group">
                  <label>设备类型</label>
                  <div class="radio-group">
                    <label class="radio-label">
                      <input type="radio" v-model="transferForm.isMobile" :value="true" /> 手游
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="transferForm.isMobile" :value="false" /> PC
                    </label>
                  </div>
                </div>
                <div class="price-preview" v-if="calculatedPrice > 0">
                  <p>转单价格：<span class="price-value">¥{{ calculatedPrice.toFixed(1) }}</span></p>
                </div>
              </div>
              <div class="confirm-footer" v-if="isWrongOrderConfirmed">
                <button class="cancel-btn" @click="closeTransferDialog">取消</button>
                <button class="confirm-btn" @click="submitTransfer">确认转单</button>
              </div>
            </div>
          </div>

          <!-- 今日完成订单 -->
          <div v-if="activeOrderTab === 'completed'" class="order-list">
            <div class="loading-state" v-if="loading">
              <div class="spinner"></div>
              <span>加载中...</span>
            </div>
            <div class="empty-state" v-else-if="completedOrders.length === 0">
              <CheckCircle size="48" class="empty-icon" />
              <p>今日暂无完成订单</p>
            </div>
            <div class="order-card" v-else v-for="order in completedOrders" :key="order.id">
              <div class="order-info">
                <div class="order-info-row">
                  <span class="info-label">订单ID</span>
                  <span class="info-value">#{{ order.id }}</span>
                </div>
                <div class="order-info-row">
                  <span class="info-label">老板ID</span>
                  <span class="info-value">{{ order.user_id }}</span>
                </div>
                <div class="order-info-row">
                  <span class="info-label">商品内容</span>
                  <span class="info-value desc-text">{{ order.desc || order.content || '无' }}</span>
                </div>
              </div>
              <div class="order-main">
                <div class="order-title">{{ order.title }}</div>
                <div class="order-price">¥{{ order.price }}</div>
              </div>
            </div>
          </div>

          <!-- 转单 -->
          <div v-if="activeOrderTab === 'transferred'" class="order-list">
            <div class="loading-state" v-if="loading">
              <div class="spinner"></div>
              <span>加载中...</span>
            </div>
            <div class="empty-state" v-else-if="transferredOrders.length === 0">
              <Share2 size="48" class="empty-icon" />
              <p>暂无转单记录</p>
            </div>
            <div class="order-card" v-else v-for="order in transferredOrders" :key="order.id">
              <div class="order-header">
                <div class="order-shop">
                  <span class="shop-name">{{ order.shop_name || '星屿小筑旗舰店' }}</span>
                </div>
                <div class="order-price">
                  <span class="price-symbol">¥</span>
                  <span class="price-value">{{ order.new_price || order.price }}</span>
                </div>
              </div>
              <div class="order-body">
                <div class="order-row">
                  <span class="order-label">状态</span>
                  <span class="order-value status-transferred">已转单</span>
                </div>
                <div class="order-row">
                  <span class="order-label">转单时间</span>
                  <span class="order-value">{{ formatTime(order.transfer_time) }}</span>
                </div>
                <div class="order-row" v-if="order.original_price">
                  <span class="order-label">原价</span>
                  <span class="order-value">¥{{ order.original_price }}</span>
                </div>
                <div class="order-row" v-if="order.fine_amount">
                  <span class="order-label">罚款</span>
                  <span class="order-value" style="color: #ef4444">¥{{ order.fine_amount }}</span>
                </div>
                <div class="order-row">
                  <span class="order-label">原因</span>
                  <span class="order-value">{{ order.transfer_reason }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 罚款记录 -->
        <div class="section">
          <h3 class="section-title">
            <AlertTriangle size="18" class="title-icon" />
            罚款记录
          </h3>
          <div class="loading-state" v-if="loading">
            <div class="spinner"></div>
            <span>加载中...</span>
          </div>
          <div class="empty-state" v-else-if="fines.length === 0">
            <CheckCircle size="48" class="empty-icon" />
            <p>暂无罚款记录</p>
          </div>
          <div class="fine-list" v-else>
            <div class="fine-card" v-for="fine in fines" :key="fine.id">
              <div class="fine-header">
                <span class="fine-reason">{{ fine.reason }}</span>
                <span class="fine-amount">-¥{{ Number(fine.amount).toFixed(1) }}</span>
              </div>
              <div class="fine-body">
                <span class="fine-time">{{ formatTime(fine.created_at) }}</span>
                <span class="fine-status" :class="{ paid: fine.paid }">
                  {{ fine.paid ? '已支付' : '未支付' }}
                </span>
              </div>
              <div class="fine-footer" v-if="!fine.paid">
                <button class="pay-fine-btn" @click="payFineHandler()">
                  缴纳
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 押金管理 -->
        <div class="section">
          <h3 class="section-title">
            <Lock size="18" class="title-icon" />
            押金管理
          </h3>
          <div class="deposit-card">
            <div class="deposit-header">
              <span class="deposit-label">当前押金</span>
              <span class="deposit-amount">¥{{ deposit.current }}</span>
            </div>
            <div class="deposit-body">
              <div class="deposit-row">
                <span class="deposit-item-label">押金状态</span>
                <span class="deposit-item-value" :class="{ active: deposit.current > 0 }">
                  {{ deposit.current > 0 ? '已缴纳' : '未缴纳' }}
                </span>
              </div>
            </div>
            <div class="deposit-actions">
              <button v-if="deposit.current === 0" class="deposit-btn pay" @click="showPayDepositDialog">
                缴纳押金
              </button>
              <button v-else class="deposit-btn refund" @click="showRefundDepositDialog">
                退回押金
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 弹窗组件 -->
    <Dialog
      v-model:visible="dialogVisible"
      :title="dialogTitle"
      :message="dialogMessage"
      :type="dialogType"
      @close="closeDialog"
    />

    <!-- 聊天通知 -->
    <ChatNotification
      ref="chatNotification"
      @open-chat="openChatFromNotification"
    />

    <!-- 聊天弹窗（有有效订单ID时才挂载，避免空订阅） -->
    <ChatDialog
      v-if="chatVisible && chatOrderId !== null && chatOrderId !== undefined"
      :visible="chatVisible"
      :order-id="chatOrderId"
      @close="closeChat"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onActivated, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowLeft, User, LogOut, ClipboardList, Wallet, Star, CheckCircle, Share2, AlertTriangle, Lock, Calendar, DollarSign, Copy, MessageSquare } from 'lucide-vue-next'
import { supabase, isFetchNetworkError } from '@/utils/supabase'
import Dialog from '@/components/Dialog.vue'
import ChatDialog from '@/components/ChatDialog.vue'
import ChatNotification from '@/components/ChatNotification.vue'
import { setSecureStorage, getSecureStorage, removeSecureStorage, validateUsername, validatePassword, SECURE_STORAGE_KEY, PLATFORM_TABLE_MAP } from '@/utils/security'
import { chatManager } from '@/utils/chatManager'
import { getAllCompletedOrders, getOrdersByStatus, getGoodsInfo, getTransferOrders, getFines, getDeposit, createFine, clearOrderCache, createTransferOrder, getXianyuOrdersByStatus, getAllXianyuCompletedOrders, updateXianyuOrderStatus } from '@/api/orders'

const router = useRouter()

// 登录状态
const isLoggedIn = ref(false)
const loading = ref(false)
const loginForm = ref({
  username: '',
  password: ''
})

// 弹窗状态
const dialogVisible = ref(false)
const dialogMessage = ref('')
const dialogType = ref('info')
const dialogTitle = ref('提示')

// 复制状态
const copiedBossId = ref('')

// 显示弹窗
const showDialog = (message, type = 'info', title = '提示') => {
  dialogMessage.value = message
  dialogType.value = type
  dialogTitle.value = title
  dialogVisible.value = true
}

// 关闭弹窗
const closeDialog = () => {
  dialogVisible.value = false
}

// 复制老板ID
async function copyBossId(bossId) {
  if (!bossId) return
  try {
    await navigator.clipboard.writeText(bossId)
    copiedBossId.value = bossId
    showDialog('老板ID已复制到剪贴板', 'success')
    setTimeout(() => {
      copiedBossId.value = ''
    }, 2000)
  } catch (err) {
    console.error('复制失败:', err)
    showDialog('复制失败，请手动复制', 'error')
  }
}

// 用户信息
const userInfo = ref({})
const stats = ref({
  totalOrders: 0,
  totalEarnings: 0,
  monthlyEarnings: 0,
  rating: 5.0
})

// 订单数据
const acceptedOrders = ref([])
const completedOrders = ref([])
const transferredOrders = ref([])
const fines = ref([])
const deposit = ref({
  player: '',
  current: 0
})
const todayOrderCount = ref(0)
const todayEarnings = ref(0)
const showCompleteDialog = ref(false)
const completeTargetOrder = ref(null)
const transferDialogVisible = ref(false)
const transferTargetOrder = ref(null)
const isWrongOrderConfirmed = ref(false)
const showProgressForm = ref(false)
const transferForm = ref({
  progress: 0,
  bombCount: 0,
  isPrison: false,
  isMobile: true
})

const calculatedPrice = computed(() => {
  if (!transferTargetOrder.value || transferForm.value.progress === 0) return 0
  const originalPrice = Number(transferTargetOrder.value.price) || 0
  const progress = Number(transferForm.value.progress) || 0
  const bombCount = Number(transferForm.value.bombCount) || 0
  const penaltyPerBomb = transferForm.value.isPrison ? 70 : 60
  const rate = transferForm.value.isMobile ? 0.07 : 0.14
  const deduction = (progress - (bombCount * penaltyPerBomb)) * rate
  return Math.max(0, originalPrice - deduction)
})

// 标签页
const activeOrderTab = ref('accepted')

// 登录函数
const isLoggingIn = ref(false)
async function login() {
  if (isLoggingIn.value) return
  isLoggingIn.value = true
  const username = loginForm.value.username.trim()
  const password = loginForm.value.password
  
  const usernameValidation = validateUsername(username)
  if (!usernameValidation.valid) {
    showDialog(usernameValidation.message, 'info')
    isLoggingIn.value = false
    return
  }
  
  const passwordValidation = validatePassword(password)
  if (!passwordValidation.valid) {
    showDialog(passwordValidation.message, 'info')
    isLoggingIn.value = false
    return
  }

  loading.value = true
  try {
    const platform = userInfo.value.platform || 'mobile_delta'
    const tableName = PLATFORM_TABLE_MAP[platform].player
    const { data: users, error } = await supabase
      .from(tableName)
      .select('username, role')
      .eq('username', username)
      .eq('password', password)
      .limit(1)
    
    if (error) {
      console.error('查询用户失败:', error)
      const message = isFetchNetworkError(error)
        ? '连接服务器失败，请检查网络后重试'
        : '登录失败: 服务器异常，请稍后重试'
      showDialog(message, 'error')
      loading.value = false
      return
    }
    
    if (users && users.length > 0) {
      isLoggedIn.value = true
      userInfo.value = {
        username: users[0].username,
        role: users[0].role || '打手',
        platform
      }
      
      setSecureStorage(SECURE_STORAGE_KEY, userInfo.value)
      
      fetchUserData()
    } else {
      showDialog('登录失败: 用户名或密码错误', 'error')
    }
  } catch (error) {
    console.error('登录失败:', error)
    const message = isFetchNetworkError(error)
      ? '连接服务器失败，请检查网络后重试'
      : '登录失败: ' + error.message
    showDialog(message, 'error')
  } finally {
    loading.value = false
    isLoggingIn.value = false
  }
}

// 登出函数
function logout() {
  isLoggedIn.value = false
  userInfo.value = {}
  acceptedOrders.value = []
  completedOrders.value = []
  transferredOrders.value = []
  fines.value = []
  deposit.value = {
    current: 0,
    status: 'inactive',
    paid_at: null,
    refunded_at: null
  }
  removeSecureStorage(SECURE_STORAGE_KEY)
}

// 获取订单商品信息
async function enrichOrderWithGoods(order) {
  if (!order.goods_id) {
    return { ...order, desc: '无商品信息' }
  }
  
  const { data: goodsData, error: goodsError } = await getGoodsInfo(order.goods_id)
  
  if (goodsError) {
    console.error('查询商品信息失败:', goodsError)
    return { ...order, desc: '商品信息查询失败' }
  }
  
  if (goodsData && goodsData.length > 0) {
    return { ...order, desc: goodsData[0].desc }
  }
  
  return { ...order, desc: '无商品信息' }
}

// 获取用户数据
async function fetchUserData() {
  loading.value = true
  try {
    clearOrderCache()
    const handlerId = userInfo.value.username
    const platform = userInfo.value.platform || 'mobile_delta'
    
    // 分批请求，避免登录瞬间 6 个查询 + CORS 预检齐发导致连接被重置
    // 第一批：普通订单（处理中 / 待审核 / 已完成）
    const [acceptedResult, reviewResult, completedResult] = await Promise.all([
      getOrdersByStatus(handlerId, 'processing', platform),
      getOrdersByStatus(handlerId, 'pending_review', platform),
      getAllCompletedOrders(handlerId, platform)
    ])

    // 第二批：闲鱼订单
    const [xianyuAcceptedResult, xianyuReviewResult, xianyuCompletedResult] = await Promise.all([
      getXianyuOrdersByStatus(handlerId, 'processing', platform),
      getXianyuOrdersByStatus(handlerId, 'pending_review', platform),
      getAllXianyuCompletedOrders(handlerId, platform)
    ])
    
    // 处理已接订单（包含处理中和待审核）
    const acceptedData = [...(acceptedResult.data || []), ...(reviewResult.data || [])]
    const xianyuAcceptedData = [...(xianyuAcceptedResult.data || []), ...(xianyuReviewResult.data || [])]
    
    // 转换 xianyu_order 数据格式
    const convertedXianyuAccepted = xianyuAcceptedData.map(order => ({
      ...order,
      id: 'xianyu_' + order.id,
      desc: order.content || order.shop_name || '无商品信息',
      table_source: 'xianyu_order'
    }))
    
    acceptedOrders.value = await Promise.all([
      ...acceptedData.map(order => enrichOrderWithGoods(order))
    ])
    acceptedOrders.value = [...acceptedOrders.value, ...convertedXianyuAccepted]
    
    // 初始化所有已接订单的聊天频道（保持后台监听）
    await initializeChatChannels()
    
    // 处理完成订单
    const allCompletedOrders = completedResult.data || []
    const xianyuCompletedData = xianyuCompletedResult.data || []
    
    const convertedXianyuCompleted = xianyuCompletedData.map(order => ({
      ...order,
      id: 'xianyu_' + order.id,
      desc: order.content || order.shop_name || '无商品信息',
      table_source: 'xianyu_order',
      finished_at: order.fish_time
    }))
    
    const mergedCompletedOrders = [...allCompletedOrders, ...convertedXianyuCompleted]
    mergedCompletedOrders.sort((a, b) => new Date(b.finished_at || b.fish_time) - new Date(a.finished_at || a.fish_time))
    
    // 筛选今日订单
    const today = new Date()
    const todayYear = today.getFullYear()
    const todayMonth = today.getMonth() + 1
    const todayDay = today.getDate()
    
    const todayOrders = mergedCompletedOrders.filter(order => {
      const timeField = order.finished_at || order.fish_time
      if (!timeField) return false
      const finishDate = new Date(timeField)
      return finishDate.getFullYear() === todayYear &&
             finishDate.getMonth() + 1 === todayMonth &&
             finishDate.getDate() === todayDay
    })
    
    // 处理今日订单数据，区分普通订单和闲鱼订单
    const normalTodayOrders = todayOrders.filter(o => !o.table_source)
    const xianyuTodayOrders = todayOrders.filter(o => o.table_source === 'xianyu_order')
    
    const enrichedNormalToday = await Promise.all(
      normalTodayOrders.map(order => enrichOrderWithGoods(order))
    )
    completedOrders.value = [...enrichedNormalToday, ...xianyuTodayOrders]
    
    // 计算今日订单数和今日收益（需×0.83抽成）
    todayOrderCount.value = completedOrders.value.length
    todayEarnings.value = completedOrders.value.reduce((total, order) => total + (order.price || 0) * 0.83, 0)
    
    // 并行获取其他数据
    const [transferResult, finesResult, depositResult] = await Promise.all([
      getTransferOrders(handlerId, platform),
      getFines(handlerId, platform),
      getDeposit(handlerId, platform)
    ])
    
    transferredOrders.value = transferResult.data || []
    fines.value = finesResult.data || []
    
    if (depositResult.data && depositResult.data.length > 0) {
      deposit.value = depositResult.data[0]
    }

    // 计算统计数据（总订单和总收益包含所有历史数据，总收益需×0.83抽成）
    stats.value.totalOrders = acceptedOrders.value.length + mergedCompletedOrders.length
    stats.value.totalEarnings = mergedCompletedOrders.reduce((sum, order) => {
      return sum + (Number(order.price) || 0) * 0.83
    }, 0)
    
    // 计算本月收益
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    stats.value.monthlyEarnings = mergedCompletedOrders.reduce((sum, order) => {
      const orderDate = new Date(order.finished_at || order.fish_time || order.created_at)
      if (orderDate >= startOfMonth) {
        return sum + (Number(order.price) || 0) * 0.83
      }
      return sum
    }, 0)
  } catch (error) {
    console.error('获取用户数据失败:', error)
  } finally {
    loading.value = false
  }
}

// 格式化时间
function formatTime(timeStr) {
  if (!timeStr) return '-'
  const date = new Date(timeStr)
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 显示结单确认弹窗
function showCompleteConfirm(order) {
  completeTargetOrder.value = order
  showCompleteDialog.value = true
}

// 关闭结单确认弹窗
function closeCompleteDialog() {
  showCompleteDialog.value = false
  completeTargetOrder.value = null
}

// 打开聊天窗口
function openChat(order) {
  chatOrderId.value = order.id
  chatVisible.value = true
}

// 关闭聊天窗口
function closeChat() {
  chatVisible.value = false
  chatOrderId.value = null
}

// 显示转单弹窗
function showTransferDialog(order) {
  transferTargetOrder.value = order
  isWrongOrderConfirmed.value = false
  showProgressForm.value = false
  transferForm.value = {
    progress: 0,
    bombCount: 0,
    isPrison: false,
    isMobile: true
  }
  transferDialogVisible.value = true
}

// 关闭转单弹窗
function closeTransferDialog() {
  transferDialogVisible.value = false
  transferTargetOrder.value = null
  isWrongOrderConfirmed.value = false
  showProgressForm.value = false
}

// 确认是点错单子
const isConfirmingWrongOrder = ref(false)
async function confirmWrongOrder() {
  if (!transferTargetOrder.value) return
  if (isConfirmingWrongOrder.value) return
  isConfirmingWrongOrder.value = true

  try {
    const orderIdStr = String(transferTargetOrder.value.id)
    const isXianyuOrder = orderIdStr.startsWith('xianyu_')
    const actualOrderId = isXianyuOrder ? orderIdStr.replace('xianyu_', '') : orderIdStr
    const platform = userInfo.value.platform || 'mobile_delta'
    const tables = PLATFORM_TABLE_MAP[platform]

    let updateError
    
    if (isXianyuOrder) {
      const result = await supabase
        .from(tables.xianyu_order)
        .update({
          status: 'pending',
          player: null
        })
        .eq('id', actualOrderId)
      updateError = result.error
    } else {
      const result = await supabase
        .from(tables.orders)
        .update({
          status: 'pending',
          handler_id: null
        })
        .eq('id', actualOrderId)
      updateError = result.error
    }

    if (updateError) {
      showDialog('转单失败: ' + updateError.message, 'error')
      return
    }

    acceptedOrders.value = acceptedOrders.value.filter(o => o.id !== transferTargetOrder.value.id)

    const transferOrder = {
      ...transferTargetOrder.value,
      status: 'pending',
      transfer_time: new Date().toISOString(),
      transfer_reason: '点错单子'
    }
    transferredOrders.value.unshift(transferOrder)

    showDialog('转单成功，订单已退回接单大厅', 'success')
    closeTransferDialog()
  } catch (error) {
    console.error('转单失败:', error)
    showDialog('转单失败: ' + error.message, 'error')
  } finally {
    isConfirmingWrongOrder.value = false
  }
}

// 确认不是点错单子，需要填写进度
function confirmNotWrongOrder() {
  isWrongOrderConfirmed.value = true
}

// 提交进度转单
const isTransferring = ref(false)

async function submitTransfer() {
  if (!transferTargetOrder.value) return
  if (isTransferring.value) return
  isTransferring.value = true

  if (transferForm.value.progress === 0 || transferForm.value.progress === '') {
    showDialog('请填写进度', 'error')
    isTransferring.value = false
    return
  }

  if (transferForm.value.bombCount === '' || transferForm.value.bombCount < 0) {
    showDialog('请填写炸单数', 'error')
    isTransferring.value = false
    return
  }

  try {
    const originalPrice = Number(transferTargetOrder.value.price)
    const newPrice = calculatedPrice.value
    const orderIdStr = String(transferTargetOrder.value.id)
    const isXianyuOrder = orderIdStr.startsWith('xianyu_')
    const actualOrderId = isXianyuOrder ? orderIdStr.replace('xianyu_', '') : orderIdStr
    const platform = userInfo.value.platform || 'mobile_delta'
    const tables = PLATFORM_TABLE_MAP[platform]

    let updateError
    
    if (isXianyuOrder) {
      const result = await supabase
        .from(tables.xianyu_order)
        .update({
          status: 'pending',
          player: null,
          price: newPrice
        })
        .eq('id', actualOrderId)
      updateError = result.error
    } else {
      const result = await supabase
        .from(tables.orders)
        .update({
          status: 'pending',
          handler_id: null,
          price: newPrice
        })
        .eq('id', actualOrderId)
      updateError = result.error
    }

    if (updateError) {
      showDialog('转单失败: ' + updateError.message, 'error')
      return
    }

    let fineAmount = 0
    const priceDiff = newPrice - originalPrice
    
    if (priceDiff < 0) {
      fineAmount = 7
    } else {
      fineAmount = Number((priceDiff + 7).toFixed(1))
    }

    if (fineAmount > 0 && userInfo.value.username) {
      const { data: newFine, error: fineError } = await createFine(
        userInfo.value.username,
        fineAmount,
        `转单罚款：进度${transferForm.value.progress}W，炸单${transferForm.value.bombCount}次`,
        transferTargetOrder.value.id,
        platform
      )

      if (!fineError) {
        const fineToAdd = newFine && newFine[0] 
          ? newFine[0] 
          : {
              id: Date.now(),
              player: userInfo.value.username,
              amount: fineAmount,
              reason: `转单罚款：进度${transferForm.value.progress}W，炸单${transferForm.value.bombCount}次`,
              order_id: transferTargetOrder.value.id,
              paid: false,
              created_at: new Date().toISOString()
            }
        fines.value.unshift(fineToAdd)
      } else {
        console.error('创建罚款记录失败:', fineError)
        showDialog('创建罚款记录失败: ' + fineError.message, 'error')
      }
    }

    acceptedOrders.value = acceptedOrders.value.filter(o => o.id !== transferTargetOrder.value.id)

    // 保存转单记录到数据库
    const transferTime = new Date().toISOString()
    const transferReason = `进度${transferForm.value.progress}W，炸单${transferForm.value.bombCount}次`
    
    const { data: newTransfer, error: transferError } = await createTransferOrder(
      transferTargetOrder.value.id,
      userInfo.value.username,
      transferTime,
      transferReason,
      originalPrice,
      newPrice,
      fineAmount,
      platform
    )

    if (transferError) {
      showDialog('创建转单记录失败: ' + transferError.message, 'error')
      return
    }

    // 添加到前端显示
    const transferOrder = newTransfer && newTransfer[0] 
      ? {
          ...transferTargetOrder.value,
          ...newTransfer[0],
          id: transferTargetOrder.value.id
        }
      : {
          ...transferTargetOrder.value,
          status: 'pending',
          price: newPrice,
          transfer_time: transferTime,
          transfer_reason: transferReason,
          fine_amount: fineAmount
        }
    transferredOrders.value.unshift(transferOrder)

    const message = fineAmount > 0 
      ? `转单成功！新价格：¥${newPrice.toFixed(1)}，罚款：¥${fineAmount}`
      : `转单成功！新价格：¥${newPrice.toFixed(1)}`
    showDialog(message, 'success')
    closeTransferDialog()
  } catch (error) {
    console.error('转单失败:', error)
    showDialog('转单失败: ' + error.message, 'error')
  } finally {
    isTransferring.value = false
  }
}

// 缴纳罚款
function payFineHandler() {
  showDialog('请联系群管理员进行罚款缴纳', 'info')
}

// 显示缴纳押金弹窗
function showPayDepositDialog() {
  showDialog('请联系管理员进行押金充值', 'info', '缴纳押金')
}

// 显示退回押金弹窗
function showRefundDepositDialog() {
  showDialog('退回押金需要退俱乐部，退前接单十单以上且未有未交罚款，请联系管理员进行相关验证', 'info', '退回押金')
}

// 确认结单
const isCompleting = ref(false)

// 聊天状态
const chatVisible = ref(false)
const chatOrderId = ref(null)

// 通知组件引用
const chatNotification = ref(null)

// 从通知打开聊天
function openChatFromNotification(orderId) {
  chatOrderId.value = orderId
  chatVisible.value = true
}

// 初始化所有已接订单的聊天频道
async function initializeChatChannels() {
  console.log('[MyPage] 初始化聊天频道')
  chatManager.init(supabase)
  
  // 设置全局通知处理器
  chatManager.setNotificationHandler((orderId, message) => {
    if (chatNotification.value) {
      chatNotification.value.addNotification(orderId, message)
    }
  })
  
  // 为每个已接订单订阅聊天频道
  for (const order of acceptedOrders.value) {
    if (order.id) {
      console.log(`[MyPage] 订阅订单 ${order.id} 的聊天频道`)
      // 订阅但不传递回调（由组件处理）
      chatManager.subscribe(order.id, null)
    }
  }
}

async function confirmComplete() {
  if (!completeTargetOrder.value) {
    showDialog('请选择要结单的订单', 'error')
    return
  }
  if (isCompleting.value) {
    return
  }
  isCompleting.value = true

  const order = completeTargetOrder.value
  const earnings = Number((order.price * 0.83).toFixed(1))
  const orderIdStr = String(order.id)
  const isXianyuOrder = orderIdStr.startsWith('xianyu_')
  const actualOrderId = isXianyuOrder ? orderIdStr.replace('xianyu_', '') : orderIdStr
  const platform = userInfo.value.platform || 'mobile_delta'

  const isHighValue = order.price > 300
  const targetStatus = isHighValue ? 'pending_review' : (isXianyuOrder ? 'finished' : 'completed')

  try {
    let updateError

    if (isXianyuOrder) {
      const result = await updateXianyuOrderStatus(actualOrderId, targetStatus, platform)
      updateError = result.error
    } else {
      const tables = PLATFORM_TABLE_MAP[platform]
      const now = new Date().toISOString()
      const updateData = isHighValue
        ? { status: 'pending_review', finished_at: now }
        : { status: 'completed', finished_at: now }
      const result = await supabase
        .from(tables.orders)
        .update(updateData)
        .eq('id', actualOrderId)
      updateError = result.error
    }

    if (updateError) {
      showDialog('结单失败: ' + updateError.message, 'error')
      return
    }

    if (isHighValue) {
      showDialog('订单金额超过300元，已提交审核，等待管理员确认', 'info', '待审核')
      order.status = 'pending_review'
    } else {
      // 从已接订单列表中移除
      acceptedOrders.value = acceptedOrders.value.filter(o => o.id !== order.id)

      // 清理该订单的聊天资源（取消订阅频道，释放内存）
      chatManager.cleanupOrder(order.id)

      // 添加到今日完成订单列表（如果是今天结单的）
      const today = new Date()
      const finishedDate = new Date()
      if (finishedDate.getFullYear() === today.getFullYear() &&
          finishedDate.getMonth() === today.getMonth() &&
          finishedDate.getDate() === today.getDate()) {
        // 更新订单状态为 completed/finished 并设置 finished_at/fish_time
        const completedOrder = {
          ...order,
          status: isXianyuOrder ? 'finished' : 'completed',
          finished_at: isXianyuOrder ? undefined : finishedDate.toISOString(),
          fish_time: isXianyuOrder ? finishedDate.toISOString() : undefined
        }
        completedOrders.value.unshift(completedOrder)
      }

      // 更新统计数据
      todayOrderCount.value = completedOrders.value.length
      todayEarnings.value = completedOrders.value.reduce((total, o) => total + (o.price || 0) * 0.83, 0)
      stats.value.totalOrders += 1
      stats.value.totalEarnings += earnings

      // 关闭弹窗
      closeCompleteDialog()

      // 显示成功消息
      showDialog(`结单成功！实际收益：¥${earnings.toFixed(1)}`, 'success')
    }
  } catch (error) {
    console.error('结单失败:', error)
    showDialog('结单失败: ' + error.message, 'error')
  } finally {
    isCompleting.value = false
  }
}

// 防止 onMounted/onActivated/watch 短时间内重复触发初始化，造成大量并发请求
let isInitializing = false
async function initializePage() {
  if (isInitializing) return
  isInitializing = true
  try {
    clearOrderCache()

    const savedUser = getSecureStorage(SECURE_STORAGE_KEY)

    if (savedUser) {
      isLoggedIn.value = true
      userInfo.value = savedUser
      await fetchUserData()
    } else {
      isLoggedIn.value = false
      userInfo.value = {}
    }
  } finally {
    isInitializing = false
  }
}

onMounted(async () => {
  await initializePage()

  // 监听从通知点击打开聊天的事件
  window.addEventListener('chatOrderSelected', handleChatOrderSelected)

  // 检查是否有从通知跳转过来的订单
  const openChatOrderId = localStorage.getItem('openChatOrderId')
  if (openChatOrderId) {
    localStorage.removeItem('openChatOrderId')
    setTimeout(() => {
      handleChatOrderSelected({ detail: { orderId: openChatOrderId } })
    }, 200)
  }
})

onActivated(async () => {
  await initializePage()
})

watch(() => router.currentRoute.value.path, (newPath) => {
  if (newPath === '/mypage') {
    initializePage()
  }
})

// 处理从通知跳转过来的聊天订单
function handleChatOrderSelected(e) {
  const orderId = e.detail.orderId
  console.log('[MyPage] 从通知打开聊天:', orderId)
  if (orderId) {
    openChatFromNotification(orderId)
  }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  padding-bottom: 100px;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: rgba(15, 17, 23, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 100;
}

.back-btn {
  background: none;
  border: none;
  color: var(--color-text-primary);
  cursor: pointer;
  padding: 8px;
  border-radius: var(--radius-md);
  transition: var(--transition-fast);
  display: flex;
  align-items: center;
}

.back-btn:hover {
  background: rgba(99, 102, 241, 0.1);
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.header-placeholder {
  width: 36px;
}

.content {
  padding: 72px 16px 20px;
  max-width: 480px;
  margin: 0 auto;
}

/* 登录部分 */
.login-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 70vh;
}

.login-card {
  background: var(--color-bg-card);
  border-radius: var(--radius-xl);
  padding: 32px 24px;
  width: 100%;
  max-width: 360px;
  border: 1px solid var(--color-border);
  text-align: center;
}

.login-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  color: var(--color-accent-primary);
}

.login-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 8px;
}

.login-subtitle {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 32px;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.form-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-size: 14px;
  transition: var(--transition-fast);
}

.form-input:focus {
  border-color: var(--color-accent-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.login-btn {
  padding: 14px;
  background: var(--color-accent-gradient);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
  margin-top: 8px;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-register {
  margin-top: 16px;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.register-link {
  background: none;
  border: none;
  color: var(--color-accent-primary);
  font-weight: 600;
  cursor: pointer;
  margin-left: 4px;
  padding: 0;
}

/* 用户信息 */
.user-card {
  background: var(--color-bg-card);
  border-radius: var(--radius-xl);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  border: 1px solid var(--color-border);
}

.user-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--color-accent-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-text {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}

.user-info {
  flex: 1;
}

.user-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 4px 0;
}

.user-role {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0;
}

.logout-btn {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  border: none;
  padding: 8px;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-fast);
}

.logout-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* 统计卡片 */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--color-border);
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: rgba(99, 102, 241, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent-primary);
}

.stat-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.stat-label {
  font-size: 11px;
  color: var(--color-text-muted);
}

/* 章节 */
.section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 17px;
  font-weight: 600;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.title-icon {
  color: var(--color-accent-primary);
}

/* 订单标签页 */
.order-tabs {
  display: flex;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  padding: 4px;
  margin-bottom: 16px;
  border: 1px solid var(--color-border);
}

.tab-btn {
  flex: 1;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-muted);
  border-radius: var(--radius-md);
  transition: var(--transition-fast);
  background: none;
  border: none;
  cursor: pointer;
}

.tab-btn.active {
  background: var(--color-accent-gradient);
  color: #fff;
}

/* 加载状态 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  gap: 12px;
  color: var(--color-text-muted);
  font-size: 13px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--color-border);
  border-top-color: var(--color-accent-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 0;
  gap: 12px;
}

.empty-icon {
  color: var(--color-text-muted);
  opacity: 0.5;
}

.empty-state p {
  font-size: 14px;
  color: var(--color-text-secondary);
}

/* 订单列表 */
.order-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.order-card {
  background: var(--color-bg-card);
  border-radius: var(--radius-xl);
  padding: 16px;
  border: 1px solid var(--color-border);
  transition: var(--transition-fast);
}

.order-card:hover {
  border-color: var(--color-border-hover);
}

/* 订单信息区域 */
.order-info {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.order-info-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-label {
  font-size: 12px;
  color: var(--color-text-muted);
}

.info-value {
  font-size: 12px;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.desc-text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
  max-height: 60px;
  overflow-y: auto;
  flex: 1;
}

/* 老板ID样式 */
.boss-id-row {
  margin-top: 4px;
}

.boss-id-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.boss-id {
  font-size: 8px;
  font-weight: 600;
  color: var(--color-accent-primary);
  font-family: monospace;
  background: rgba(99, 102, 241, 0.1);
  padding: 4px 10px;
  border-radius: var(--radius-sm);
}

.copy-boss-id-btn {
  padding: 4px 8px;
  background: var(--color-accent-primary);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-boss-id-btn:hover {
  background: var(--color-accent-primary-dark);
}

.copy-boss-id-btn.copied {
  background: var(--color-success);
}

/* 订单主体区域 */
.order-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--color-bg-tertiary);
  border-radius: var(--radius-md);
}

.order-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  flex: 1;
}

.order-main .order-price {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-accent-primary);
}

/* 内容字段样式 */
.order-content {
  margin-top: 8px;
}

.order-content .order-value {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
  max-height: 120px;
  overflow-y: auto;
}

/* 结单按钮 */
.order-footer {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: flex-end;
}

.complete-btn {
  padding: 8px 20px;
  background: var(--color-accent-gradient);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
}

.complete-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

/* 结单确认弹窗 */
.confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.confirm-card {
  background: var(--color-bg-card);
  border-radius: var(--radius-xl);
  padding: 24px;
  width: 90%;
  max-width: 360px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.confirm-header {
  margin-bottom: 20px;
}

.confirm-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.confirm-body {
  margin-bottom: 24px;
}

.confirm-body p {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 8px 0;
}

.earnings-info {
  font-weight: 600;
  color: var(--color-text-primary);
}

.earnings-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-success);
}

.confirm-footer {
  display: flex;
  gap: 12px;
}

.cancel-btn,
.confirm-btn {
  flex: 1;
  padding: 12px;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
}

.cancel-btn {
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
}

.cancel-btn:hover {
  background: var(--color-bg-hover);
}

.confirm-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  border: none;
}

.confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.transfer-question {
  font-size: 16px;
  color: var(--color-text-primary);
  text-align: center;
  margin-bottom: 20px;
}

.transfer-buttons {
  display: flex;
  gap: 12px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
}

.form-group input[type="number"] {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
  font-size: 14px;
}

.form-group input[type="number"]:focus {
  outline: none;
  border-color: var(--color-accent-primary);
}

.radio-group {
  display: flex;
  gap: 20px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
  color: var(--color-text-primary);
}

.radio-label input[type="radio"] {
  cursor: pointer;
}

.price-preview {
  background: var(--color-bg-tertiary);
  padding: 12px;
  border-radius: var(--radius-md);
  text-align: center;
  margin-top: 16px;
}

.price-preview p {
  font-size: 14px;
  color: var(--color-text-secondary);
}

.price-preview .price-value {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-accent-primary);
}

.chat-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
}

.chat-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.transfer-btn {
  background: #f59e0b;
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 8px;
}

.review-status {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #fff;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border);
}

.order-shop {
  display: flex;
  align-items: center;
  gap: 8px;
}

.shop-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.order-price {
  display: flex;
  align-items: baseline;
}

.price-symbol {
  font-size: 13px;
  color: var(--color-accent-primary);
  font-weight: 600;
}

.price-value {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-accent-primary);
}

.order-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.order-label {
  font-size: 12px;
  color: var(--color-text-muted);
  min-width: 60px;
}

.order-value {
  font-size: 13px;
  color: var(--color-text-secondary);
  flex: 1;
}

.status-processing {
  color: var(--color-warning);
  font-weight: 500;
}

.status-completed {
  color: var(--color-success);
  font-weight: 500;
}

.status-transferred {
  color: var(--color-accent-primary);
  font-weight: 500;
}

/* 罚款记录 */
.fine-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fine-card {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  padding: 14px;
  border: 1px solid var(--color-border);
}

.fine-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.fine-reason {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.fine-amount {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-error);
}

.fine-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.fine-time {
  font-size: 12px;
  color: var(--color-text-muted);
}

.fine-status {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-warning);
}

.fine-status.paid {
  color: var(--color-success);
}

/* 押金管理 */
.deposit-card {
  background: var(--color-bg-card);
  border-radius: var(--radius-xl);
  padding: 20px;
  border: 1px solid var(--color-border);
}

.deposit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border);
}

.deposit-label {
  font-size: 14px;
  color: var(--color-text-secondary);
}

.deposit-amount {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.deposit-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.deposit-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.deposit-item-label {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.deposit-item-value {
  font-size: 13px;
  color: var(--color-text-muted);
}

.deposit-item-value.active {
  color: var(--color-success);
  font-weight: 500;
}

.deposit-actions {
  display: flex;
  gap: 12px;
}

.deposit-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
}

.deposit-btn.pay {
  background: var(--color-accent-gradient);
  color: #fff;
}

.deposit-btn.refund {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
  border: 1px solid var(--color-error);
}

.deposit-btn.disabled {
  background: var(--color-bg-tertiary);
  color: var(--color-text-muted);
  cursor: not-allowed;
}

.deposit-btn:hover:not(.disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
</style>
